import { useState } from "react";
import { Save } from "lucide-react";
import type { RetentionPolicy } from "@/types";

interface RetentionConfigProps {
  policies: RetentionPolicy[];
  onSave: (policy: Partial<RetentionPolicy>) => void;
  isSaving: boolean;
}

const retentionOptions = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "365 days" },
];

export function RetentionConfig({
  policies,
  onSave,
  isSaving,
}: RetentionConfigProps) {
  const [editedPolicies, setEditedPolicies] = useState<
    Record<string, number>
  >(() => {
    const map: Record<string, number> = {};
    for (const p of policies) {
      map[p.tier] = p.retention_days;
    }
    return map;
  });

  const handleChange = (tier: string, days: number) => {
    setEditedPolicies((prev) => ({ ...prev, [tier]: days }));
  };

  const handleSave = (tier: string) => {
    const policy = policies.find((p) => p.tier === tier);
    if (policy) {
      onSave({ ...policy, retention_days: editedPolicies[tier] });
    }
  };

  if (policies.length === 0) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
        <h3 className="text-lg font-semibold text-gray-200">
          Retention Policy
        </h3>
        <p className="mt-2 text-sm text-gray-400">
          No retention policies configured. Policies are set per service tier.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
      <h3 className="mb-4 text-lg font-semibold text-gray-200">
        Backup Retention Policy
      </h3>
      <div className="space-y-4">
        {policies.map((policy) => (
          <div
            key={policy.tier}
            className="flex items-center justify-between rounded-lg border border-gray-800 p-4"
          >
            <div>
              <p className="font-medium text-gray-200">Tier {policy.tier}</p>
              <p className="text-xs text-gray-500">
                Schedule: {policy.schedule_cron} | Max backups:{" "}
                {policy.max_backups}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={editedPolicies[policy.tier] ?? policy.retention_days}
                onChange={(e) =>
                  handleChange(policy.tier, Number(e.target.value))
                }
                className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 outline-none focus:border-blue-500"
              >
                {retentionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleSave(policy.tier)}
                disabled={
                  isSaving ||
                  editedPolicies[policy.tier] === policy.retention_days
                }
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />
                Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
